import {
  Profile,
  SocialLinksCommonInterface,
  SocialLinksEnum,
  StoreImage,
  StoreImageEnum,
  getCommonItem,
  getStoreImageHeight,
  getStoreImageWidth,
} from '@systemeio/digital-store-shared'
import { FileSourceEnum } from '@systemeio/file-manager'
import { FormInput, PrimaryButton, toast } from '@systemeio/ui'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import FormInputSocial from 'modules/stores/store/content/profile/components/form-input-social'
import { useProfileActions } from 'modules/stores/store/content/profile/hooks/use-profile-actions'
import {
  ProfileEditInterface,
  ProfileUpdateInterface,
} from 'modules/stores/store/content/profile/types/profile-interface'
import { editSocialLinksArr } from 'modules/stores/store/content/profile/utils/edit-social-links-arr'
import {
  SCHEME,
  getSocialLinkLabel,
} from 'modules/stores/store/content/profile/utils/get-social-link-label'
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { transformData } from 'modules/stores/store/utils/transform-data'
import { StoreInterface } from 'modules/stores/types/stores-interface'
import React, { FC, useEffect, useState } from 'react'
import { AvatarUploader } from 'shared/components/file-uploader/avatar-uploader'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { deepCompare } from 'shared/utils/deepCompare'
import { isValidUrl } from 'shared/utils/is-valid-url'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { twMerge } from 'tailwind-merge'
import { ArrowDownIcon } from '@systemeio/ui/icons'

interface ProfileEditProps {
  store: StoreInterface
}

const baseSocialMedias: SocialLinksEnum[] = [SocialLinksEnum.instagram, SocialLinksEnum.tiktok]

const restSocialMedias: SocialLinksEnum[] = Object.values(SocialLinksEnum).filter(
  media => !baseSocialMedias.includes(media),
)

const ProfileEdit: FC<ProfileEditProps> = ({ store }) => {
  const { t } = useLocoTranslation()

  const { storeId, redirectToMainPage } = useStoreQuery()

  const { mutate } = useStore(storeId)

  const [errors, setErrors] = useState<Record<string, string>>({})

  const { profile, socialLinks } = store

  const [tempProfile, setTempProfile] = useState<ProfileEditInterface['profile']>(profile)

  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)
  const { transformProfileToStoreData } = transformData()

  const { updateProfile } = useProfileActions()

  const [tempSocialLinks, setTempSocialLinks] =
    useState<Array<SocialLinksCommonInterface>>(socialLinks)

  const instagram = tempSocialLinks.find(media => media.type === SocialLinksEnum.instagram)
  const tiktok = tempSocialLinks.find(media => media.type === SocialLinksEnum.tiktok)

  const [showMoreSocials, setShowMoreSocials] = useState(false)

  const [isFetching, setIsFetching] = useState(false)

  const [linkErrors, setLinkErrors] = useState<Record<SocialLinksEnum, ''>>(
    Object.values(SocialLinksEnum).reduce(
      (acc, curr) => ({ ...acc, [curr]: '' }),
      {} as Record<SocialLinksEnum, ''>,
    ),
  )

  const { setPreview, setSingleItemMode } = useStorePreviewContext()

  useEffect(() => {
    setPreview([
      <Profile key={store.id} profile={tempProfile} socialLinks={tempSocialLinks} />,
      ...store.items.filter(el => !el.draft).map(getCommonItem),
    ])
    setSingleItemMode(false)
  }, [setPreview, setSingleItemMode, store, tempProfile, tempSocialLinks])

  const onSave = async () => {
    try {
      setErrors({})
      setIsFetching(true)
      const { sellerPhoto, ...profileToUpload } = tempProfile
      const profile: ProfileUpdateInterface['profile'] = {
        ...profileToUpload,
        sellerPhotoId: sellerPhoto?.id ?? null,
      }
      const responseData = await updateProfile(store.id, {
        profile,
        socialLinks: tempSocialLinks,
      })
      await mutate(
        data =>
          data && { ...data, profile: responseData.profile, socialLinks: responseData.socialLinks },
        false,
      )
      setIsFetching(false)
      toast.success(t('global.changes_saved'))
      await redirectToMainPage()
    } catch (e) {
      handle422Error(e, setErrors)
    } finally {
      setIsFetching(false)
    }
  }

  const validateSocialLink = (link: SocialLinksEnum, url: string) => {
    if (getSocialLinkLabel(t, link) === SCHEME) {
      if (url && !isValidUrl(url)) {
        setLinkErrors(prev => ({
          ...prev,
          [link]: t('dashboard.settings.affiliate_program.invalid_url'),
        }))
      } else {
        setLinkErrors(prev => ({ ...prev, [link]: '' }))
      }
    }
  }

  const getSocialLinkError = (link: SocialLinksEnum) => {
    const indexOfSocialMedia = tempSocialLinks.findIndex(media => media.type === link)
    const typeError = errors[`socialLinks[${indexOfSocialMedia}].type`] ?? ''
    const urlError = errors[`socialLinks[${indexOfSocialMedia}].url`] ?? ''
    const serverError = typeError + urlError
    if (serverError) {
      return serverError
    } else {
      return linkErrors[link]
    }
  }

  useEffect(() => {
    setIsDataSaved(
      deepCompare(transformProfileToStoreData(tempProfile, tempSocialLinks, store), store),
    )
  }, [tempProfile, tempSocialLinks])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <div className={'flex items-center justify-center'}>
        <AvatarUploader
          width={getStoreImageWidth(StoreImageEnum.profile)}
          height={getStoreImageHeight(StoreImageEnum.profile)}
          onSetPhoto={dataFile =>
            setTempProfile(prev => ({
              ...prev,
              sellerPhoto: dataFile,
            }))
          }
          source={FileSourceEnum.DigitalStore}
        >
          <StoreImage
            imageType={StoreImageEnum.profile}
            alt={tempProfile.sellerName}
            thumbnail={tempProfile.sellerPhoto}
            className={'border-brand-primary rounded-full border-2 object-cover'}
            skipTheme
          />
        </AvatarUploader>
      </div>
      <div className={'flex flex-col gap-3'}>
        <FormInput
          label={t('dashboard.store.profile.display_name')}
          value={tempProfile.sellerName}
          placeholder={t('dashboard.user.display_name')}
          onChange={e => setTempProfile(prev => ({ ...prev, sellerName: e.target.value }))}
          error={errors['profile.sellerName']}
        />
        <FormInput
          label={t('dashboard.store.bio')}
          placeholder={t('dashboard.store.bio')}
          value={tempProfile.bio}
          onChange={e => setTempProfile(prev => ({ ...prev, bio: e.target.value }))}
          error={errors['profile.bio']}
        />
      </div>
      <div className={'flex flex-col gap-3'}>
        <span>{t('dashboard.store.profile.social_links')}</span>
        <div className={'flex flex-col gap-3'}>
          <FormInputSocial
            social={SocialLinksEnum.instagram}
            value={instagram?.url}
            onChange={value =>
              setTempSocialLinks(prev =>
                editSocialLinksArr(prev, {
                  type: SocialLinksEnum.instagram,
                  url: value,
                }),
              )
            }
            error={getSocialLinkError(SocialLinksEnum.instagram)}
          />
          <FormInputSocial
            social={SocialLinksEnum.tiktok}
            value={tiktok?.url}
            onChange={value =>
              setTempSocialLinks(prev =>
                editSocialLinksArr(prev, { type: SocialLinksEnum.tiktok, url: value }),
              )
            }
            error={getSocialLinkError(SocialLinksEnum.tiktok)}
          />
        </div>
        <button
          className={
            'ring-brand-primary flex items-center justify-between rounded-lg outline-hidden ring-offset-2 focus-visible:ring-2'
          }
          onClick={() => setShowMoreSocials(!showMoreSocials)}
        >
          <span className={'text-brand-primary text-lg'}>
            {showMoreSocials
              ? t('dashboard.store.profile.show_less_socials')
              : t('dashboard.store.profile.show_more_socials')}
          </span>
          <ArrowDownIcon
            className={`stroke-brand-primary h-4 w-4 ${
              showMoreSocials && 'rotate-180'
            } transition-transform`}
          />
        </button>
        {showMoreSocials && (
          <div className={'flex flex-col gap-3'}>
            {restSocialMedias.map(social => {
              const media = tempSocialLinks.find(link => link.type === social)
              return (
                <FormInputSocial
                  key={social}
                  social={social}
                  value={media?.url}
                  onChange={value =>
                    setTempSocialLinks(prev =>
                      editSocialLinksArr(prev, { type: social, url: value }),
                    )
                  }
                  onBlur={() => validateSocialLink(social, media?.url || '')}
                  error={getSocialLinkError(social)}
                />
              )
            })}
          </div>
        )}
      </div>
      <PrimaryButton
        disabled={Object.values(linkErrors).some(error => !!error)}
        isFetching={isFetching}
        onClick={onSave}
        className={'w-fit self-end px-10'}
      >
        {t('global.save')}
      </PrimaryButton>
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.store.confirmation_message_before_exit')}
        hideModal={isDataSaved || isFetching}
      />
    </div>
  )
}

export default ProfileEdit
